import {
  Fragment,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  useCallback,
  useState,
} from "react";

import clsx from "clsx";
import FuzzySearch from "fuzzy-search";
import { isEqual } from "lodash";
import { twMerge } from "tailwind-merge";

import { RowVirtualizerDynamic } from "@components/virtualized-list";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Transition,
} from "@headlessui/react";

import { createSearchHighlightedLabel, renderChevronAnimation } from "./utils";

export type OptionType<Value = unknown> = {
  label: string;
  value: Value;
  disabled?: boolean;
};

interface SelectProps<Value, T = any> {
  comboboxRefSetter?: (node: HTMLElement | null) => void;
  className?: string;
  defaultValue?: OptionType<Value>["value"];
  formatDisplayValue?: (option?: OptionType<Value>) => string;
  hideCaret?: boolean;
  icon?: ReactNode;
  isClearable?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  isSearchable?: boolean;
  label?: string | ReactElement;
  maxWidth?: string;
  name?: string;
  onChange: (value: OptionType<Value>["value"]) => void;
  options: OptionType<Value>[];
  placeholder?: string | ReactElement;
  tabIndex?: number | string;
  value: OptionType<Value>["value"];
  width?: string;
  height?: string;
  testId?: string;
  immediate?: boolean;
  autoFocus?: boolean;
  onClose?: () => void;

  // tooltip
  tooltip?: {
    tooltip: (props: PropsWithChildren<T>) => JSX.Element;
    tooltipProps: T;
  };

  // TODO: Determine to add or delete these props
  errorMessage?: string;
  errors?: any;

  menuItemRender?: ({
    option,
    value,
    onChange,
    active,
    selected,
    search,
  }: {
    option: OptionType<Value>;
    value: OptionType<Value>["value"];
    onChange: (value: OptionType<Value>["value"]) => void;
    active: boolean;
    selected: boolean;
    search: string;
  }) => React.ReactElement;
  labelClassname?: string;
  menuClassName?: string;
  menuTitle?: string;
  buttonRenderer?: ({
    open,
    displayValue,
    value,
  }: {
    open: boolean;
    displayValue?: string;
    value: OptionType<Value>["value"];
  }) => React.ReactElement;
  comparator?: (a: Value, b: Value) => boolean;
  activeInputClassName?: string;
  noPortal?: boolean;
  isRequired?: boolean;
}

export type ExtendableSelectProps = Omit<
  SelectProps<unknown>,
  "value" | "options"
>;

const MAX_NUMBER = 200;

export function Select<Value = unknown>({
  comboboxRefSetter,
  className,
  activeInputClassName,
  defaultValue,
  formatDisplayValue,
  hideCaret = false,
  icon,
  isClearable = false,
  isDisabled = false,
  isLoading = false,
  isSearchable = false,
  isRequired = false,
  immediate = false,
  autoFocus = false,
  onClose,
  label,
  tooltip,
  maxWidth,
  menuClassName,
  buttonRenderer,
  menuItemRender,
  menuTitle,
  name,
  onChange,
  options,
  placeholder = "Select...",
  tabIndex,
  testId,
  value,
  width,
  height,
  comparator,
  labelClassname,
  noPortal,
}: SelectProps<Value>) {
  const searcher = new FuzzySearch(options, ["label"], { sort: true });
  const [search, setSearch] = useState("");

  const foundOption = options.find((option) =>
    comparator
      ? comparator(option?.value, value)
      : isEqual(option?.value, value),
  );

  const displayValue = formatDisplayValue?.(foundOption) ?? foundOption?.label;
  const getDisplayValue = (open: boolean) => {
    if (isSearchable && open) {
      return null;
    }
    return displayValue || placeholder;
  };

  const filteredOptions = searcher.search(search) as OptionType<Value>[];

  const SelectRow = useCallback(
    ({ row: option }: { row: any }) => {
      return (
        <ComboboxOption
          key={JSON.stringify(option.value)}
          value={option.value}
          disabled={option.disabled}
          as={Fragment}
        >
          {({ focus, selected }) =>
            menuItemRender ? (
              menuItemRender({
                option,
                value,
                onChange,
                active: focus,
                selected,
                search,
              })
            ) : (
              <div
                className={clsx(
                  (focus || selected) && !option.disabled
                    ? "bg-v2-highlight-information font-semibold"
                    : "hover:bg-v2-highlight-information",
                  option.disabled && "bg-gray-3 pointer-events-none",
                  "px-4 py-2 cursor-pointer text-v2-primary font-normal",
                )}
                dangerouslySetInnerHTML={createSearchHighlightedLabel(
                  option.label,
                  search,
                )}
              />
            )
          }
        </ComboboxOption>
      );
    },
    [menuItemRender, onChange, search, value],
  );

  const Tooltip = tooltip?.tooltip;
  return (
    <div
      className={clsx(
        "nb-input-container text-sm",
        {
          "pointer-events-none": isLoading || isDisabled,
        },
        className,
      )}
      style={{ width, maxWidth, height }}
      tabIndex={Number(tabIndex) || undefined}
      data-testid={testId}
    >
      {label && !tooltip && (
        <label
          className={twMerge(
            clsx("label text-v2-primary", isDisabled && "text-disabled"),
            labelClassname,
          )}
        >
          {label}
          {isRequired && <span className="text-mars">*</span>}
        </label>
      )}
      {label && Tooltip && (
        <Tooltip {...tooltip.tooltipProps}>
          <label className={twMerge("label", labelClassname)}>
            {label}
            {isRequired && <span className="text-mars">*</span>}
          </label>
        </Tooltip>
      )}
      <div ref={comboboxRefSetter}>
        <Combobox
          name={name}
          disabled={isDisabled}
          value={value}
          defaultValue={defaultValue}
          onChange={onChange}
          immediate={immediate}
          onClose={onClose}
        >
          {({ open }) => (
            <>
              {buttonRenderer ? (
                buttonRenderer({ open, displayValue, value })
              ) : (
                <ComboboxButton
                  as="div"
                  className={clsx(
                    "nb-input flex justify-between items-center cursor-pointer bg-v2-primary",
                    {
                      [activeInputClassName ??
                      "border border-v2-action ring-1 ring-cosmic"]: open,
                      "opacity-60": isDisabled,
                    },
                    {
                      "bg-v2-primary border-v2-secondary text-v2-primary":
                        !activeInputClassName,
                    },
                    className,
                  )}
                >
                  <div
                    className={clsx(
                      "text-ellipsis whitespace-nowrap overflow-hidden max-w-full flex-grow",
                      "text-left",
                      !search &&
                        !displayValue &&
                        placeholder &&
                        "text-disabled",
                    )}
                  >
                    <ComboboxInput
                      className={clsx("bg-transparent", {
                        "w-full": isSearchable && open,
                        "w-0": !isSearchable || !open,
                      })}
                      autoComplete="off"
                      value={search}
                      onKeyDown={(e) => {
                        if (e.code === "Space") {
                          e.stopPropagation();
                        }
                      }}
                      onChange={({ target }) => setSearch(target.value)}
                      autoFocus={autoFocus}
                    />
                    {getDisplayValue(open)}
                  </div>
                  {isClearable && Boolean(value) && (
                    <i
                      className={clsx(
                        "fa-regular fa-sharp fa-times cursor-pointer",
                        "hover:text-v2-action px-0.5 py-2",
                      )}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onChange(null as any);
                      }}
                    />
                  )}
                  {!hideCaret && (
                    <span
                      className={clsx(
                        { "text-cosmic": open },
                        "pl-2 justify-self-end",
                      )}
                    >
                      {renderChevronAnimation(open, isLoading, icon)}
                    </span>
                  )}
                </ComboboxButton>
              )}
              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
                afterLeave={() => setSearch("")}
              >
                <ComboboxOptions
                  anchor={
                    noPortal
                      ? undefined
                      : { to: "bottom start", gap: 13, offset: -13 }
                  }
                  className={clsx(
                    "absolute overflow-y-auto z-[9998] px-0 py-1 rounded-lg elevation-1 bg-v2-primary",
                    "text-sm min-w-[var(--button-width)]",
                    menuClassName ? menuClassName : "max-h-[20rem]",
                    { "mt-2": noPortal },
                  )}
                >
                  {menuTitle && (
                    <div className="text-xs font-light text-gray+1 px-4 py-2">
                      {menuTitle}
                    </div>
                  )}
                  {filteredOptions.length === 0 && (
                    <div className="px-4 py-2 text-disabled">
                      NO RESULTS FOUND
                    </div>
                  )}
                  {filteredOptions.length > MAX_NUMBER && (
                    <div className="min-h-[19rem] h-full">
                      <RowVirtualizerDynamic
                        rows={filteredOptions}
                        Row={SelectRow}
                      />
                    </div>
                  )}
                  {filteredOptions.length <= MAX_NUMBER &&
                    filteredOptions.map((option) => (
                      <ComboboxOption
                        key={JSON.stringify(option.value)}
                        value={option.value}
                        disabled={option.disabled}
                        as={Fragment}
                      >
                        {({ focus, selected }) =>
                          menuItemRender ? (
                            menuItemRender({
                              option,
                              value,
                              onChange,
                              active: focus,
                              selected,
                              search,
                            })
                          ) : (
                            <div
                              className={clsx(
                                (focus || selected) && !option.disabled
                                  ? "bg-cosmic-3 font-semibold"
                                  : "hover:bg-v2-highlight-information",
                                option.disabled &&
                                  "bg-gray-3 pointer-events-none",
                                "px-4 py-2 cursor-pointer text-v2-primary font-normal",
                              )}
                              dangerouslySetInnerHTML={createSearchHighlightedLabel(
                                option.label,
                                search,
                              )}
                            />
                          )
                        }
                      </ComboboxOption>
                    ))}
                </ComboboxOptions>
              </Transition>
            </>
          )}
        </Combobox>
      </div>
    </div>
  );
}
